import { TextareaHTMLAttributes, forwardRef, useId } from 'react';
import * as Label from '@radix-ui/react-label';
import { cn } from '@/design-system/utils/cn';

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ label, error, className, id, ...props }, ref) => {
    const generatedId = useId();
    const inputId = id ?? generatedId;
    return (
      <div className="w-full">
        {label && (
          <Label.Root htmlFor={inputId} className="block text-sm font-medium text-gray-700 mb-1.5">
            {label}
          </Label.Root>
        )}
        <textarea
          ref={ref}
          id={inputId}
          aria-invalid={!!error}
          aria-describedby={error ? `${inputId}-error` : props['aria-describedby']}
          className={cn(
            'w-full px-3 py-2 text-sm border border-gray-200 bg-white shadow-sm rounded-lg',
            'focus:outline-none focus:border-primary focus-visible:ring-1 focus-visible:ring-primary/30',
            'disabled:bg-gray-100 disabled:cursor-not-allowed',
            'resize-y min-h-[80px]',
            error && 'border-red-500 focus:border-red-500 focus-visible:ring-red-500/30',
            className
          )}
          {...props}
        />
        {error && (
          <p id={`${inputId}-error`} role="alert" className="mt-1 text-sm text-red-600">{error}</p>
        )}
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';
